// ── Pet Egg Ambient Glow — pure stage computation ──
//
// Maps eggBondingSeconds to visual parameters for the HomeScene egg glow.
// No Phaser imports. Tested independently.

import { BONDING_CUES } from '@game/shared';

/**
 * Glow parameters for a given bonding stage.
 */
export interface EggGlowParams {
  /** 1-based bonding stage (1..6). */
  stage: number;
  /** Glow pulse frequency in Hz. */
  pulseHz: number;
  /** Glow color as a hex number (0xRRGGBB). */
  glowColor: number;
  /** Whether the egg should rock (stage >= 5). */
  rocking: boolean;
  /** Diegetic cue text for proximity tooltip. */
  cueText: string;
}

// ── Stage color endpoints ───────────────────────────────────────────
// Amber (#C4825A) at stage 1, gold (#DAA520) at stage 6.
const AMBER_R = 0xc4, AMBER_G = 0x82, AMBER_B = 0x5a;
const GOLD_R = 0xda, GOLD_G = 0xa5, GOLD_B = 0x20;

// Pulse Hz endpoints: stage 1 = 0.3Hz, stage 6 = 1.5Hz (linear).
const MIN_PULSE_HZ = 0.3;
const MAX_PULSE_HZ = 1.5;

/**
 * Determine which 1-based stage (1..6) the bonding seconds fall into,
 * using the BONDING_CUES thresholds from shared types.
 * Returns 0 if bondingSeconds <= 0 (no egg / unclaimed).
 */
export function bondingStage(bondingSeconds: number): number {
  if (bondingSeconds <= 0) return 0;
  for (let i = 0; i < BONDING_CUES.length; i++) {
    if (bondingSeconds <= BONDING_CUES[i].maxSeconds) return i + 1;
  }
  // Past all cue thresholds (hatch-ready), treat as final stage.
  return BONDING_CUES.length;
}

function lerpByte(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

/**
 * Compute all glow parameters from bonding seconds and cue text.
 * Returns null when there is no egg to display (stage 0).
 */
export function computeEggGlow(
  bondingSeconds: number,
  cueText: string,
): EggGlowParams | null {
  const stage = bondingStage(bondingSeconds);
  if (stage === 0) return null;

  // t in [0, 1] across stages 1..6
  const t = (stage - 1) / (BONDING_CUES.length - 1);

  const r = lerpByte(AMBER_R, GOLD_R, t);
  const g = lerpByte(AMBER_G, GOLD_G, t);
  const b = lerpByte(AMBER_B, GOLD_B, t);
  const glowColor = (r << 16) | (g << 8) | b;

  const pulseHz = MIN_PULSE_HZ + (MAX_PULSE_HZ - MIN_PULSE_HZ) * t;

  return {
    stage,
    pulseHz,
    glowColor,
    rocking: stage >= 5,
    cueText,
  };
}
